/**
 * FilterPanelContent - Pure filter UI body for desktop right panel
 * Extracted from mobile FilterDrawerOverlay - same UI, no drawer shell.
 * @version 1.0.0
 */
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Check, ChevronDown, ChevronUp, ChevronRight, Building2, Tag, Layers } from 'lucide-react';
import type { RangeFilter } from '../../../hooks/useDiscoveryFiltersState';
import type { FacetItem } from '../types';

export interface FilterPanelContentProps {
  onApply: () => void;
  onClear: () => void;
  onClearAll: () => void;
  searchQuery: string;
  selectedIndustryIds: string[];
  selectedCategoryIds: string[];
  selectedSubcategoryIds: string[];
  onToggleIndustry: (id: string) => void;
  onToggleCategory: (id: string) => void;
  onToggleSubcategory: (id: string) => void;
  commissionRange: RangeFilter;
  priceRange: RangeFilter;
  discountRange: RangeFilter;
  onCommissionRangeChange: (range: RangeFilter) => void;
  onPriceRangeChange: (range: RangeFilter) => void;
  onDiscountRangeChange: (range: RangeFilter) => void;
  industryFacets: FacetItem[];
  categoryFacets: FacetItem[];
  subcategoryFacets: FacetItem[];
  /** Hide commission range slider (e.g. customer-facing surfaces) */
  showCommission?: boolean;
  creatorIndustryIds?: string[];
  totalResultCount?: number;
  coverageBounds?: {
    priceBounds: { min: number; max: number };
    commissionBounds: { min: number; max: number };
    discountBounds: { min: number; max: number };
  };
}

interface RankedIndustry {
  id: string;
  label: string;
  count: number;
  score: number;
  isSelected: boolean;
}

export const FilterPanelContent: React.FC<FilterPanelContentProps> = ({
  onApply,
  onClear,
  onClearAll,
  searchQuery,
  selectedIndustryIds,
  selectedCategoryIds,
  selectedSubcategoryIds,
  onToggleIndustry,
  onToggleCategory,
  onToggleSubcategory,
  commissionRange,
  priceRange,
  discountRange,
  onCommissionRangeChange,
  onPriceRangeChange,
  onDiscountRangeChange,
  industryFacets,
  categoryFacets,
  subcategoryFacets,
  showCommission = true,
  creatorIndustryIds = [],
  totalResultCount = 0,
  coverageBounds,
}) => {
  const [industriesExpanded, setIndustriesExpanded] = React.useState(false);

  const priceBounds = coverageBounds?.priceBounds ?? { min: 0, max: 1000 };
  const commissionDollarBounds = coverageBounds?.commissionBounds ?? { min: 0, max: 100 };
  const discountDollarBounds = coverageBounds?.discountBounds ?? { min: 0, max: 100 };

  const availableCategories = React.useMemo(
    () => categoryFacets.map((c) => ({ id: c.id, label: c.label, count: c.count ?? 0 })),
    [categoryFacets],
  );

  const showCategories = selectedIndustryIds.length > 0;
  const showSubcategories = selectedCategoryIds.length > 0;

  const rankedIndustries = React.useMemo((): RankedIndustry[] => {
    const query = searchQuery.toLowerCase().trim();
    const creatorSet = new Set(creatorIndustryIds);
    const selectedSet = new Set(selectedIndustryIds);
    const ranked: RankedIndustry[] = [];

    for (const facet of industryFacets) {
      const count = facet.count ?? 0;
      const isSelected = selectedSet.has(facet.id);
      if (count === 0 && !isSelected) continue;
      let score = 0;
      if (isSelected) score += 1000;
      if (creatorSet.has(facet.id)) score += 100;
      if (query && facet.label.toLowerCase().includes(query)) score += 50;
      score += Math.min(count, 50);
      ranked.push({ id: facet.id, label: facet.label, count, score, isSelected });
    }
    ranked.sort((a, b) => b.score - a.score);
    return ranked;
  }, [industryFacets, selectedIndustryIds, creatorIndustryIds, searchQuery]);

  const COLLAPSED_LIMIT = 8;
  const EXPANDED_LIMIT = 20;
  const visibleIndustries = rankedIndustries.slice(0, industriesExpanded ? EXPANDED_LIMIT : COLLAPSED_LIMIT);
  const hasMoreIndustries = rankedIndustries.length > COLLAPSED_LIMIT;

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Range sliders */}
        <FilterSection title="Earnings & Deal Terms">
          <div className="space-y-2">
            {showCommission && (
              <RangeSlider label="Commission" value={commissionRange} onChange={onCommissionRangeChange} bounds={commissionDollarBounds} step={1} formatValue={(v) => `$${Math.round(v)}`} />
            )}
            <RangeSlider label="Price" value={priceRange} onChange={onPriceRangeChange} bounds={priceBounds} step={5} formatValue={(v) => `$${Math.round(v)}`} />
            <RangeSlider label="Discount" value={discountRange} onChange={onDiscountRangeChange} bounds={discountDollarBounds} step={1} formatValue={(v) => `$${Math.round(v)}`} />
          </div>
        </FilterSection>

        {/* Industries */}
        <FilterSection title="Industries" icon={<Building2 className="w-3.5 h-3.5" />}>
          <div className="grid grid-cols-2 gap-1.5">
            {visibleIndustries.map((item) => (
              <button
                key={item.id}
                onClick={() => onToggleIndustry(item.id)}
                className={cn(
                  'h-9 px-2.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 min-w-0 select-none',
                  item.isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-foreground border border-border hover:bg-muted',
                )}
              >
                {item.isSelected && <Check className="w-3 h-3 shrink-0" />}
                <span className="truncate flex-1 text-left">{item.label}</span>
                <span className="opacity-60 tabular-nums shrink-0 text-[10px]">({item.count})</span>
              </button>
            ))}
          </div>
          {hasMoreIndustries && (
            <div className="flex justify-end mt-2">
              <button
                onClick={() => setIndustriesExpanded(!industriesExpanded)}
                className="px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-1"
              >
                {industriesExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {industriesExpanded ? 'less' : `more (${rankedIndustries.length})`}
              </button>
            </div>
          )}
        </FilterSection>

        {/* Categories */}
        {showCategories && (
          <FilterSection title="Categories" icon={<Tag className="w-3.5 h-3.5" />}>
            {availableCategories.length > 0 ? (
              <div className="grid grid-cols-2 gap-1.5">
                {availableCategories.map((cat) => {
                  const isSelected = selectedCategoryIds.includes(cat.id);
                  const isDisabled = cat.count === 0 && !isSelected;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => !isDisabled && onToggleCategory(cat.id)}
                      disabled={isDisabled}
                      className={cn(
                        'h-9 px-2.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 min-w-0 select-none',
                        isSelected ? 'bg-primary/10 text-primary border border-primary/20'
                          : isDisabled ? 'bg-muted/50 text-muted-foreground/50 cursor-not-allowed'
                          : 'bg-card text-foreground border border-border hover:bg-muted',
                      )}
                    >
                      {isSelected && <Check className="w-3 h-3 shrink-0" />}
                      <span className="truncate flex-1 text-left">{cat.label}</span>
                      <span className="text-muted-foreground tabular-nums shrink-0 text-[10px]">({cat.count})</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">No categories in current range</p>
            )}
          </FilterSection>
        )}

        {/* Subcategories */}
        {showSubcategories && subcategoryFacets.length > 0 && (
          <FilterSection title="Subcategories" icon={<Layers className="w-3.5 h-3.5" />}>
            <div className="grid grid-cols-2 gap-1.5">
              {subcategoryFacets.map((item) => {
                const isSelected = selectedSubcategoryIds.includes(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => onToggleSubcategory(item.id)}
                    className={cn(
                      'h-9 px-2.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 min-w-0 select-none',
                      isSelected ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'bg-card text-foreground border border-border hover:bg-muted',
                    )}
                  >
                    {isSelected && <Check className="w-3 h-3 shrink-0" />}
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0 text-[10px]">({item.count})</span>
                  </button>
                );
              })}
            </div>
          </FilterSection>
        )}
      </div>

      {/* Sticky footer */}
      <div className="shrink-0 border-t border-border px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <button onClick={onClearAll} className="text-xs font-medium text-destructive hover:text-destructive/80">Clear All</button>
          <button onClick={onClear} className="text-xs font-medium text-muted-foreground hover:text-foreground">Reset</button>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            Items: <span className="font-semibold text-foreground">{totalResultCount.toLocaleString()}</span>
          </span>
          <button
            data-panel-apply="true"
            onClick={onApply}
            className="flex-1 max-w-[140px] py-2 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            APPLY
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== Helper components ===== */

const FilterSection: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="bg-card rounded-xl border border-border p-3">
    <h3 className="text-xs font-semibold text-foreground mb-2.5 flex items-center gap-1.5">{icon}{title}</h3>
    {children}
  </div>
);

interface RangeSliderProps {
  label: string;
  value: RangeFilter;
  onChange: (range: RangeFilter) => void;
  bounds: { min: number; max: number };
  step: number;
  formatValue: (value: number) => string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({ label, value, onChange, bounds, step, formatValue }) => {
  const effectiveMin = value.min ?? bounds.min;
  const effectiveMax = value.max ?? bounds.max;
  const clampedMin = Math.max(bounds.min, Math.min(effectiveMin, bounds.max - step));
  const clampedMax = Math.min(bounds.max, Math.max(effectiveMax, bounds.min + step));
  const isDisabled = bounds.min >= bounds.max;

  return (
    <div className={cn('mb-6 last:mb-0', isDisabled && 'opacity-50 pointer-events-none')}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
          {formatValue(clampedMin)} - {formatValue(clampedMax)}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <input type="range" min={bounds.min} max={bounds.max} step={step} value={clampedMin} disabled={isDisabled}
            onChange={(e) => onChange({ ...value, min: Math.min(Number(e.target.value), clampedMax - step) })}
            className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-grab" />
          <span className="block mt-1 text-[10px] text-muted-foreground text-center">Min</span>
        </div>
        <div className="flex-1">
          <input type="range" min={bounds.min} max={bounds.max} step={step} value={clampedMax} disabled={isDisabled}
            onChange={(e) => onChange({ ...value, max: Math.max(Number(e.target.value), clampedMin + step) })}
            className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-grab" />
          <span className="block mt-1 text-[10px] text-muted-foreground text-center">Max</span>
        </div>
      </div>
    </div>
  );
};

export default FilterPanelContent;
